// Updated RosterPage.js with deduplication functionality
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";

function RosterPage() {
  const { teamId } = useParams();
  const [user] = useAuthState(auth);
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [rosterError, setRosterError] = useState("");
  const [team, setTeam] = useState({ name: "", sport: "" });
  const [canManagePlayers, setCanManagePlayers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("playerName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterStatus, setFilterStatus] = useState("all");
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Function to find and merge duplicate roster entries
  const deduplicateRoster = async (teamId, email) => {
    try {
      console.log(`Looking for duplicates for ${email} in team ${teamId}`);
      
      // Query for all roster entries with matching email and team
      const rosterRef = collection(db, "rosters");
      const duplicatesQuery = query(
        rosterRef,
        where("teamId", "==", teamId),
        where("playerEmail", "==", email)
      );
      
      const duplicateSnap = await getDocs(duplicatesQuery);
      
      if (duplicateSnap.size <= 1) {
        console.log("No duplicates found");
        return;
      }
      
      console.log(`Found ${duplicateSnap.size} entries for the same player`);
      
      // Collect all entries
      const entries = duplicateSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Find the most recent active entry (if any)
      const activeEntries = entries.filter(entry => entry.status === "active");
      const pendingEntries = entries.filter(entry => entry.status === "pending");
      
      // Keep the active entry if it exists, otherwise keep the most recent entry
      let entryToKeep;
      if (activeEntries.length > 0) {
        // Sort by createdAt and keep the most recent active entry
        entryToKeep = activeEntries.sort((a, b) => 
          (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0)
        )[0];
      } else if (pendingEntries.length > 0) {
        // If no active entries, keep the most recent pending one
        entryToKeep = pendingEntries.sort((a, b) => 
          (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0)
        )[0];
      } else {
        // Otherwise keep the most recent entry
        entryToKeep = entries.sort((a, b) => 
          (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0)
        )[0];
      }
      
      console.log("Keeping entry:", entryToKeep.id);
      
      // Delete all other entries
      const deletePromises = entries
        .filter(entry => entry.id !== entryToKeep.id)
        .map(entry => {
          console.log("Deleting duplicate entry:", entry.id);
          return deleteDoc(doc(db, "rosters", entry.id));
        });
      
      await Promise.all(deletePromises);
      
      console.log("Deduplication complete");
      return entryToKeep.id;
    } catch (error) {
      console.error("Error in deduplication:", error);
      throw error;
    }
  };

  // Check for duplicates on initial load
  useEffect(() => {
    const checkForDuplicates = async () => {
      if (!teamId) return;
      
      try {
        const rosterQuery = query(
          collection(db, "rosters"),
          where("teamId", "==", teamId)
        );
        
        const snapshot = await getDocs(rosterQuery);
        
        // Group entries by email to find duplicates
        const emailMap = {};
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.playerEmail) {
            if (!emailMap[data.playerEmail]) {
              emailMap[data.playerEmail] = [];
            }
            emailMap[data.playerEmail].push({
              id: doc.id,
              ...data
            });
          }
        });
        
        // Deduplicate any entries with more than one record
        const deduplicationPromises = [];
        for (const [email, entries] of Object.entries(emailMap)) {
          if (entries.length > 1) {
            console.log(`Found ${entries.length} entries for ${email}`);
            deduplicationPromises.push(deduplicateRoster(teamId, email));
          }
        }
        
        if (deduplicationPromises.length > 0) {
          await Promise.all(deduplicationPromises);
          // The roster will be refreshed by the main roster loading useEffect
          console.log("Deduplication complete");
          setRefreshTrigger(prev => prev + 1);
        }
      } catch (error) {
        console.error("Error checking for duplicates:", error);
      }
    };
    
    checkForDuplicates();
  }, [teamId]);

  // Fetch team data
  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          setTeam({
            name: teamSnap.data().name,
            sport: teamSnap.data().sport || "Team Sport",
          });
        } else {
          setRosterError("Team not found.");
        }
      } catch (err) {
        console.error("Error fetching team data:", err);
        setRosterError("Failed to load team information.");
      }
    };
    fetchTeamData();
  }, [teamId]);

  // Fetch roster along with player data
  useEffect(() => {
    setLoadingRoster(true);
    
    const rosterQuery = query(
      collection(db, "rosters"),
      where("teamId", "==", teamId)
    );
    
    const unsubscribe = onSnapshot(
      rosterQuery,
      async (snapshot) => {
        try {
          const rosterList = snapshot.docs.map((rDoc) => ({
            id: rDoc.id,
            ...rDoc.data(),
          }));

          const withPlayerData = await Promise.all(
            rosterList.map(async (entry) => {
              if (!entry.playerId) {
                // Handle roster entries that might not have a playerId
                return {
                  ...entry,
                  playerName: entry.playerName || "Unknown Player",
                  playerEmail: entry.playerEmail || "No email provided",
                  position: entry.position || "Unassigned",
                  jerseyNumber: entry.jerseyNumber || "N/A",
                  playerImage: null,
                };
              }
              
              const playerRef = doc(db, "players", entry.playerId);
              try {
                const playerSnap = await getDoc(playerRef);
                let playerName = entry.playerName || "Unknown Player";
                let playerEmail = entry.playerEmail || "No email provided";
                let position = entry.position || "Unassigned";
                let jerseyNumber = entry.jerseyNumber || "N/A";
                let playerImage = null;

                if (playerSnap.exists()) {
                  const pData = playerSnap.data();
                  playerName = `${pData.firstName || ""} ${pData.lastName || ""}`.trim() || playerName;
                  playerEmail = pData.email || playerEmail;
                  position = entry.position || pData.position || "Unassigned";
                  jerseyNumber = entry.jerseyNumber || pData.jerseyNumber || "N/A";
                  playerImage = pData.profileImageUrl || null;
                }

                return {
                  ...entry,
                  playerName,
                  playerEmail,
                  position,
                  jerseyNumber,
                  playerImage,
                };
              } catch (playerError) {
                console.error("Error fetching player data:", playerError);
                // Return entry with default values if player fetch fails
                return {
                  ...entry,
                  playerName: entry.playerName || "Unknown Player",
                  playerEmail: entry.playerEmail || "No email provided",
                  position: entry.position || "Unassigned",
                  jerseyNumber: entry.jerseyNumber || "N/A",
                  playerImage: null,
                };
              }
            })
          );

          setRoster(withPlayerData);
          setLoadingRoster(false);
        } catch (err) {
          console.error("Error processing roster data:", err);
          setRosterError("Failed to load roster.");
          setLoadingRoster(false);
        }
      },
      (error) => {
        console.error("Error fetching roster:", error);
        setRosterError("Failed to load roster.");
        setLoadingRoster(false);
      }
    );
    
    return () => unsubscribe();
  }, [teamId, refreshTrigger]);

  // Check if user is allowed to manage players (coach or admin)
  useEffect(() => {
    if (!user) return;
    
    const checkManagerStatus = async () => {
      try {
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          // Check if user is coach
          const isCoach = teamData.coachId === user.uid;
          
          // Check if user is admin
          const isAdmin = teamData.admins && 
                         Array.isArray(teamData.admins) && 
                         teamData.admins.includes(user.uid);
          
          // Set permissions if either condition is true
          setCanManagePlayers(isCoach || isAdmin);
        }
      } catch (err) {
        console.error("Error checking manager status:", err);
        setCanManagePlayers(false);
      }
    };
    
    checkManagerStatus();
  }, [user, teamId]);

  // Remove player from roster - Updated to handle player removal from team members
  const handleRemovePlayer = async (rosterId, playerName, playerId) => {
    if (!canManagePlayers) {
      alert("You are not authorized to remove players from this team.");
      return;
    }
    
    if (window.confirm(`Are you sure you want to remove ${playerName} from the roster?`)) {
      try {
        // Debug logging to help identify the issue
        console.log("Roster deletion attempt:", {
          rosterId,
          teamId,
          userId: user?.uid,
          isAuthenticated: !!user,
        });
        
        // Get the roster document first to check its data
        const rosterRef = doc(db, "rosters", rosterId);
        const rosterDoc = await getDoc(rosterRef);
        
        if (!rosterDoc.exists()) {
          throw new Error("Roster entry no longer exists");
        }
        
        const rosterData = rosterDoc.data();
        console.log("Roster document data:", rosterData);
        
        // 1. Delete the roster entry
        await deleteDoc(rosterRef);
        
        // 2. If we have a playerId, get the player document to find their user ID
        if (playerId || rosterData.playerId) {
          const playerIdToRemove = playerId || rosterData.playerId;
          const playerRef = doc(db, "players", playerIdToRemove);
          const playerDoc = await getDoc(playerRef);
          
          if (playerDoc.exists()) {
            const playerData = playerDoc.data();
            const userIdToRemove = playerData.parentUID;
            
            // 3. Update the team document to remove this user from members
            if (userIdToRemove) {
              const teamRef = doc(db, "teams", teamId);
              const teamDoc = await getDoc(teamRef);
              
              if (teamDoc.exists()) {
                const teamData = teamDoc.data();
                const currentMembers = teamData.members || [];
                
                // Filter out the user ID
                const updatedMembers = currentMembers.filter(
                  memberId => memberId !== userIdToRemove
                );
                
                // Update the team document
                await updateDoc(teamRef, {
                  members: updatedMembers
                });
                
                console.log("Removed user from team members list:", userIdToRemove);
              }
            }
          }
        }
        
        // Update local state if Firestore operation succeeds
        setRoster((prev) => prev.filter((entry) => entry.id !== rosterId));
        alert("Player removed from roster successfully!");
      } catch (err) {
        console.error("Error removing player:", err);
        alert(`Failed to remove player: ${err.message || "Unknown error"}`);
      }
    }
  };

  // Update player status
  const handleUpdateStatus = async (rosterId, currentStatus) => {
    if (!canManagePlayers) {
      alert("You are not authorized to update player status.");
      return;
    }
    
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    
    try {
      console.log("Updating status for roster entry:", rosterId, "to", newStatus);
      await updateDoc(doc(db, "rosters", rosterId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state if Firestore operation succeeds
      setRoster(prev => 
        prev.map(entry => 
          entry.id === rosterId 
            ? { ...entry, status: newStatus } 
            : entry
        )
      );
    } catch (err) {
      console.error("Error updating player status:", err);
      alert(`Failed to update player status: ${err.message || "Unknown error"}`);
    }
  };

  // Start editing a field
  const startEditing = (rosterId, field, currentValue) => {
    if (!canManagePlayers) {
      alert("You are not authorized to edit player information.");
      return;
    }
    setEditingField(`${rosterId}-${field}`);
    setEditValue(currentValue);
  };

  // Save edited field
  const saveEditing = async (rosterId, field) => {
    if (!canManagePlayers) return;
    
    try {
      // Validate input values
      if (field === 'jerseyNumber') {
        const numValue = parseInt(editValue);
        if (isNaN(numValue) || numValue < 0 || numValue > 9999) {
          alert("Jersey number must be a number between 0 and 9999");
          return;
        }
      } else if (field === 'position') {
        if (editValue.length > 20) {
          alert("Position cannot exceed 20 characters");
          return;
        }
      }
      
      console.log("Updating field for roster entry:", rosterId, field, editValue);
      // Update in Firestore
      await updateDoc(doc(db, "rosters", rosterId), {
        [field]: editValue,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setRoster(prev => 
        prev.map(entry => 
          entry.id === rosterId 
            ? { ...entry, [field]: editValue } 
            : entry
        )
      );
      
      // Reset editing state
      setEditingField(null);
      setEditValue("");
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      alert(`Failed to update ${field}: ${err.message || "Unknown error"}`);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
  };

  // Handle key press in editing fields
  const handleKeyPress = (e, rosterId, field) => {
    if (e.key === 'Enter') {
      saveEditing(rosterId, field);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // Search, filter and sort functions
  const filteredRoster = roster
    .filter(player => {
      // Search term filter
      const searchMatch = 
        (player.playerName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (player.playerEmail?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (player.position?.toLowerCase() || "").includes(searchTerm.toLowerCase());
                         
      // Status filter
      const statusMatch = filterStatus === "all" || player.status === filterStatus;
      
      return searchMatch && statusMatch;
    })
    .sort((a, b) => {
      // Sorting
      let comparison = 0;
      
      if (sortBy === "playerName") {
        comparison = (a.playerName || "").localeCompare(b.playerName || "");
      } else if (sortBy === "position") {
        comparison = (a.position || "").localeCompare(b.position || "");
      } else if (sortBy === "jerseyNumber") {
        const numA = parseInt(a.jerseyNumber) || 0;
        const numB = parseInt(b.jerseyNumber) || 0;
        comparison = numA - numB;
      } else if (sortBy === "status") {
        comparison = (a.status || "").localeCompare(b.status || "");
      }
      
      return sortOrder === "asc" ? comparison : -comparison;
    });

  // Toggle sort order
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  // Loading and error states
  if (loadingRoster) {
    return (
      <div style={loadingContainerStyle}>
        <div style={loadingSpinnerStyle}></div>
        <p>Loading roster...</p>
      </div>
    );
  }

  if (rosterError) {
    return (
      <div style={errorContainerStyle}>
        <h3>Something went wrong</h3>
        <p>{rosterError}</p>
        <Link to="/teams" style={backButtonStyle}>
          Back to Teams
        </Link>
      </div>
    );
  }

  return (
    <div style={pageContainerStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>{team.name} Roster</h1>
          <p style={subtitleStyle}>{team.sport}</p>
        </div>
        <div style={statsBoxStyle}>
          <div style={statItemStyle}>
            <span style={statNumberStyle}>{roster.length}</span>
            <span style={statLabelStyle}>Players</span>
          </div>
          <div style={statItemStyle}>
            <span style={statNumberStyle}>
              {roster.filter(p => p.status === "active").length}
            </span>
            <span style={statLabelStyle}>Active</span>
          </div>
        </div>
      </div>

      <div style={controlsContainerStyle}>
        <div style={searchFilterStyle}>
          <input
            type="text"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
          
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        
        {canManagePlayers && (
          <Link to={`/team/${teamId}/dashboard/roster/add`} style={addButtonStyle}>
            Add New Player
          </Link>
        )}
      </div>

      {filteredRoster.length === 0 ? (
        <div style={emptyStateStyle}>
          <h3>No players found</h3>
          <p>Try a different search or filter, or add new players to the team.</p>
        </div>
      ) : (
        <div style={rosterTableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle} onClick={() => handleSort("playerName")}>
                  Player Name
                  {sortBy === "playerName" && (
                    <span style={sortIconStyle}>
                      {sortOrder === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th style={thStyle} onClick={() => handleSort("jerseyNumber")}>
                  #
                  {sortBy === "jerseyNumber" && (
                    <span style={sortIconStyle}>
                      {sortOrder === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th style={thStyle} onClick={() => handleSort("position")}>
                  Position
                  {sortBy === "position" && (
                    <span style={sortIconStyle}>
                      {sortOrder === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                <th style={thStyle}>Email</th>
                <th style={thStyle} onClick={() => handleSort("status")}>
                  Status
                  {sortBy === "status" && (
                    <span style={sortIconStyle}>
                      {sortOrder === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </th>
                {canManagePlayers && <th style={thStyle}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredRoster.map((player) => (
                <tr key={player.id} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={playerCellStyle}>
                      <div style={avatarContainerStyle}>
                        {player.playerImage ? (
                          <img 
                            src={player.playerImage} 
                            alt={player.playerName} 
                            style={avatarImageStyle} 
                          />
                        ) : (
                          <div style={avatarPlaceholderStyle}>
                            {(player.playerName || " ").charAt(0)}
                          </div>
                        )}
                      </div>
                      {player.playerId ? (
                        <Link
                          to={`/team/${teamId}/dashboard/player/${player.playerId}`}
                          style={playerLinkStyle}
                        >
                          {player.playerName}
                        </Link>
                      ) : (
                        <span style={playerNameStyle}>{player.playerName}</span>
                      )}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {editingField === `${player.id}-jerseyNumber` ? (
                      <div style={editFieldContainerStyle}>
                        <input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, player.id, 'jerseyNumber')}
                          style={editInputStyle}
                          autoFocus
                          min="0"
                          max="9999"
                        />
                        <div style={editButtonsContainerStyle}>
                          <button 
                            onClick={() => saveEditing(player.id, 'jerseyNumber')}
                            style={saveButtonStyle}
                            title="Save"
                          >
                            ✓
                          </button>
                          <button 
                            onClick={cancelEditing}
                            style={cancelButtonStyle}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        style={canManagePlayers ? editableFieldStyle : {}}
                        onClick={() => canManagePlayers && startEditing(player.id, 'jerseyNumber', player.jerseyNumber)}
                      >
                        {player.jerseyNumber}
                        {canManagePlayers && <span style={editIconStyle}>✏️</span>}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {editingField === `${player.id}-position` ? (
                      <div style={editFieldContainerStyle}>
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, player.id, 'position')}
                          style={editInputStyle}
                          autoFocus
                          maxLength={20}
                        />
                        <div style={editButtonsContainerStyle}>
                          <button 
                            onClick={() => saveEditing(player.id, 'position')}
                            style={saveButtonStyle}
                            title="Save"
                          >
                            ✓
                          </button>
                          <button 
                            onClick={cancelEditing}
                            style={cancelButtonStyle}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        style={canManagePlayers ? editableFieldStyle : {}}
                        onClick={() => canManagePlayers && startEditing(player.id, 'position', player.position)}
                      >
                        {player.position}
                        {canManagePlayers && <span style={editIconStyle}>✏️</span>}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>{player.playerEmail}</td>
                  <td style={tdStyle}>
                    <span style={getStatusBadgeStyle(player.status)}>
                      {player.status?.charAt(0).toUpperCase() + player.status?.slice(1) || "Unknown"}
                    </span>
                  </td>
                  {canManagePlayers && (
                    <td style={tdStyle}>
                      <div style={actionButtonsStyle}>
                        <button
                          onClick={() => handleUpdateStatus(player.id, player.status)}
                          style={getToggleStatusButtonStyle(player.status)}
                          title={player.status === "active" ? "Deactivate Player" : "Activate Player"}
                        >
                          {player.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          onClick={() => handleRemovePlayer(player.id, player.playerName, player.playerId)}
                          style={removeButtonStyle}
                          title="Remove Player"
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Additional style for player name without link
const playerNameStyle = {
  color: "#2563eb",
  fontWeight: "500",
};

// Additional styles for editable fields
const editableFieldStyle = {
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "2px 6px",
  borderRadius: "4px",
  border: "1px dashed transparent",
  transition: "all 0.2s ease",
  ":hover": {
    borderColor: "#d1d5db",
    backgroundColor: "#f9fafb",
  },
};

const editIconStyle = {
  fontSize: "14px",
  opacity: "0",
  transition: "opacity 0.2s ease",
  ":hover": {
    opacity: "1",
  },
};

const editFieldContainerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const editInputStyle = {
  padding: "6px 10px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  fontSize: "15px",
  width: "100%",
  maxWidth: "120px",
};

const editButtonsContainerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
};

const saveButtonStyle = {
  backgroundColor: "#10b981",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: "bold",
  border: "none",
  borderRadius: "4px",
  padding: "4px 6px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const cancelButtonStyle = {
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: "bold",
  border: "none",
  borderRadius: "4px",
  padding: "4px 6px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Original styles
const pageContainerStyle = {
  padding: "30px",
  backgroundColor: "#f9fafb",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  maxWidth: "1200px",
  margin: "0 auto",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "30px",
  flexWrap: "wrap",
  gap: "20px",
};

const titleStyle = {
  color: "#1e40af",
  margin: "0",
  fontSize: "32px",
  fontWeight: "700",
};

const subtitleStyle = {
  color: "#4b5563",
  margin: "4px 0 0 0",
  fontSize: "16px",
};

const statsBoxStyle = {
  display: "flex",
  gap: "20px",
  backgroundColor: "#ffffff",
  padding: "15px 25px",
  borderRadius: "10px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
};

const statItemStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minWidth: "80px",
};

const statNumberStyle = {
  fontSize: "28px",
  fontWeight: "700",
  color: "#1e40af",
};

const statLabelStyle = {
  fontSize: "14px",
  color: "#6b7280",
};

const controlsContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "25px",
  flexWrap: "wrap",
  gap: "15px",
};

const searchFilterStyle = {
  display: "flex",
  gap: "15px",
  flexWrap: "wrap",
};

const searchInputStyle = {
  padding: "10px 15px",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  fontSize: "16px",
  width: "250px",
  transition: "border-color 0.2s",
  outline: "none",
  boxSizing: "border-box",
};

const filterSelectStyle = {
  padding: "10px 15px",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  fontSize: "16px",
  backgroundColor: "white",
  cursor: "pointer",
};

const addButtonStyle = {
  backgroundColor: "#10b981",
  color: "#fff",
  fontSize: "16px",
  fontWeight: "600",
  border: "none",
  borderRadius: "8px",
  padding: "12px 20px",
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background-color 0.2s",
};

const rosterTableContainerStyle = {
  overflowX: "auto",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "15px",
};

const thStyle = {
  textAlign: "left",
  padding: "14px 20px",
  borderBottom: "1px solid #e5e7eb",
  color: "#4b5563",
  fontWeight: "600",
  cursor: "pointer",
  backgroundColor: "#f9fafb",
  position: "sticky",
  top: "0",
};

const tdStyle = {
  padding: "14px 20px",
  borderBottom: "1px solid #e5e7eb",
  color: "#1f2937",
};

const trStyle = {
  transition: "background-color 0.1s",
  backgroundColor: "#ffffff",
  ":hover": {
    backgroundColor: "#f9fafb",
  },
};

const playerCellStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const avatarContainerStyle = {
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const avatarImageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const avatarPlaceholderStyle = {
  width: "100%",
  height: "100%",
  backgroundColor: "#1e40af",
  color: "#ffffff",
  fontSize: "16px",
  fontWeight: "600",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const playerLinkStyle = {
  color: "#2563eb",
  textDecoration: "none",
  fontWeight: "500",
  transition: "color 0.2s",
};

const getStatusBadgeStyle = (status) => {
  const baseStyle = {
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "500",
    display: "inline-block",
  };

  if (status === "active") {
    return {
      ...baseStyle,
      backgroundColor: "#d1fae5",
      color: "#047857",
    };
  } else if (status === "inactive") {
    return {
      ...baseStyle,
      backgroundColor: "#f3f4f6",
      color: "#6b7280",
    };
  } else if (status === "pending") {
    return {
      ...baseStyle,
      backgroundColor: "#fef3c7",
      color: "#92400e",
    };
  } else {
    return {
      ...baseStyle,
      backgroundColor: "#e0f2fe",
      color: "#0369a1",
    };
  }
};

const actionButtonsStyle = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
};

const getToggleStatusButtonStyle = (status) => {
  const baseStyle = {
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "14px",
    border: "none",
    cursor: "pointer",
    fontWeight: "500",
    transition: "background-color 0.2s, opacity 0.2s",
  };

  if (status === "active") {
    return {
      ...baseStyle,
      backgroundColor: "#d1d5db",
      color: "#1f2937",
    };
  } else {
    return {
      ...baseStyle,
      backgroundColor: "#10b981",
      color: "#ffffff",
    };
  }
};

const removeButtonStyle = {
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontSize: "14px",
  fontWeight: "500",
  border: "none",
  borderRadius: "6px",
  padding: "6px 12px",
  cursor: "pointer",
  transition: "background-color 0.2s, opacity 0.2s",
};

const sortIconStyle = {
  marginLeft: "2px",
  color: "#6b7280",
};

const emptyStateStyle = {
  textAlign: "center",
  padding: "50px 20px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
};

const loadingContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "50px",
  textAlign: "center",
};

const loadingSpinnerStyle = {
  borderRadius: "50%",
  width: "40px",
  height: "40px",
  border: "4px solid #f3f3f3",
  borderTop: "4px solid #1e40af",
  animation: "spin 1s linear infinite",
  marginBottom: "15px",
};

const errorContainerStyle = {
  textAlign: "center",
  padding: "30px",
  backgroundColor: "#fee2e2",
  borderRadius: "10px",
  color: "#b91c1c",
};

const backButtonStyle = {
  display: "inline-block",
  marginTop: "15px",
  padding: "8px 16px",
  backgroundColor: "#1e40af",
  color: "#ffffff",
  borderRadius: "6px",
  textDecoration: "none",
  fontWeight: "500",
};

export default RosterPage;